import React, { useState, useEffect } from 'react';
import { X, Save, Building2, Car, Users, Loader2, Ban, Unlock, Edit2 } from 'lucide-react';
import { api } from '../../services/api';

export function CompanyDetailModal({ companyId, onClose, onAction, onRefresh }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [message, setMessage] = useState(null);
  const [form, setForm] = useState({});
  const [activeTab, setActiveTab] = useState('info');

  useEffect(() => { loadCompany(); }, [companyId]);

  const loadCompany = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/company/${companyId}/full`);
      setData(res.data);
      initForm(res.data);
    } catch {
      setMessage({ text: 'Fehler beim Laden', type: 'error' });
    } finally { setLoading(false); }
  };

  const initForm = (d) => {
    const c = d.company || {};
    setForm({
      company_name: c.company_name || '',
      contact_person: c.contact_person || '',
      phone: c.phone || '',
      region: c.region || '',
      languages: (c.languages || []).join(', '),
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const payload = { ...form };
      if (typeof payload.languages === 'string') {
        payload.languages = payload.languages.split(',').map(l => l.trim()).filter(Boolean);
      }
      await api.put(`/admin/company/${companyId}/edit`, payload);
      setMessage({ text: 'Unternehmen aktualisiert', type: 'success' });
      setEditing(false);
      await loadCompany();
      if (onRefresh) onRefresh();
    } catch (e) {
      setMessage({ text: e.response?.data?.detail || 'Fehler', type: 'error' });
    } finally { setSaving(false); }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center" onClick={onClose}>
        <div className="bg-white rounded-2xl p-8" onClick={e => e.stopPropagation()}>
          <Loader2 size={24} className="animate-spin text-orange-500" />
        </div>
      </div>
    );
  }

  const company = data?.company || {};
  const owner = data?.owner || {};
  const vehicles = company.vehicles || [];
  const drivers = company.drivers || [];

  const Field = ({ label, value, field, type = 'text' }) => (
    <div>
      <label className="block text-xs font-medium text-slate-500 mb-1">{label}</label>
      {editing && field ? (
        <input type={type} value={form[field] || ''} onChange={e => setForm(f => ({ ...f, [field]: e.target.value }))}
          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400 outline-none" data-testid={`edit-company-${field}`} />
      ) : (
        <p className="px-3 py-2 bg-slate-50 rounded-lg text-sm text-slate-800" data-testid={`company-val-${field || label}`}>{value || '-'}</p>
      )}
    </div>
  );

  const tabs = [
    { id: 'info', label: 'Firmendaten', icon: Building2 },
    { id: 'vehicles', label: `Fahrzeuge (${vehicles.length})`, icon: Car },
    { id: 'drivers', label: `Fahrer (${drivers.length})`, icon: Users },
  ];

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="company-detail-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-slate-800 flex items-center justify-center text-white">
              <Building2 size={20} />
            </div>
            <div>
              <h2 className="font-bold text-lg text-slate-800" data-testid="company-detail-name">{company.company_name}</h2>
              <p className="text-xs text-slate-400">{owner.email} | {company.phone || '-'}</p>
            </div>
            <span className={`ml-2 px-2.5 py-0.5 rounded-full text-xs font-medium ${company.status === 'blocked' ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}`}>
              {company.status === 'blocked' ? 'Gesperrt' : 'Aktiv'}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {!editing ? (
              <button onClick={() => setEditing(true)} className="px-3 py-1.5 bg-orange-500 text-white rounded-lg text-xs font-medium hover:bg-orange-600" data-testid="edit-company-btn">
                Bearbeiten
              </button>
            ) : (
              <>
                <button onClick={() => { setEditing(false); initForm(data); }} className="px-3 py-1.5 bg-slate-100 text-slate-600 rounded-lg text-xs font-medium hover:bg-slate-200">Abbrechen</button>
                <button onClick={handleSave} disabled={saving} className="px-3 py-1.5 bg-orange-500 text-white rounded-lg text-xs font-medium hover:bg-orange-600 disabled:opacity-50 flex items-center gap-1" data-testid="save-company-btn">
                  {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Speichern
                </button>
              </>
            )}
            {company.status !== 'blocked' ? (
              <button onClick={() => onAction && onAction('block', companyId, 'company')}
                className="flex items-center gap-1 px-3 py-1.5 bg-red-500 text-white rounded-lg text-xs font-medium hover:bg-red-600" data-testid="block-company-detail">
                <Ban size={12} /> Sperren
              </button>
            ) : (
              <button onClick={() => onAction && onAction('unblock', companyId, 'company')}
                className="flex items-center gap-1 px-3 py-1.5 bg-emerald-500 text-white rounded-lg text-xs font-medium hover:bg-emerald-600" data-testid="unblock-company-detail">
                <Unlock size={12} /> Entsperren
              </button>
            )}
            <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-lg" data-testid="close-company-detail"><X size={18} /></button>
          </div>
        </div>

        {/* Tabs */}
        <div className="px-6 py-2 border-b border-slate-100 flex gap-1">
          {tabs.map(t => {
            const I = t.icon;
            return (
              <button key={t.id} onClick={() => setActiveTab(t.id)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap ${activeTab === t.id ? 'bg-orange-50 text-orange-600' : 'text-slate-500 hover:bg-slate-50'}`}
                data-testid={`company-tab-${t.id}`}>
                <I size={14} /> {t.label}
              </button>
            );
          })}
        </div>

        {/* Content */}
        <div className="px-6 py-5 overflow-y-auto max-h-[55vh]">
          {message && (
            <div className={`mb-4 p-3 rounded-lg text-sm ${message.type === 'success' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
              {message.text}
            </div>
          )}

          {/* Company Info */}
          {activeTab === 'info' && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <Field label="Firmenname" value={company.company_name} field="company_name" />
                <Field label="Ansprechpartner" value={company.contact_person} field="contact_person" />
                <Field label="Telefon" value={company.phone} field="phone" type="tel" />
                <Field label="Region" value={company.region} field="region" />
                <Field label="Sprachen" value={(company.languages || []).join(', ') || '-'} field="languages" />
                <Field label="Registriert am" value={company.created_at ? new Date(company.created_at).toLocaleDateString('de-DE') : '-'} />
              </div>
              <div className="border-t border-slate-100 pt-4">
                <h4 className="text-sm font-semibold text-slate-700 mb-2">Inhaber</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div><label className="block text-xs font-medium text-slate-500 mb-1">Name</label><p className="px-3 py-2 bg-slate-50 rounded-lg text-sm text-slate-800">{owner.name || '-'}</p></div>
                  <div><label className="block text-xs font-medium text-slate-500 mb-1">E-Mail</label><p className="px-3 py-2 bg-slate-50 rounded-lg text-sm text-slate-800">{owner.email || '-'}</p></div>
                  <div><label className="block text-xs font-medium text-slate-500 mb-1">Telefon</label><p className="px-3 py-2 bg-slate-50 rounded-lg text-sm text-slate-800">{owner.phone || '-'}</p></div>
                  <div><label className="block text-xs font-medium text-slate-500 mb-1">Rolle</label><p className="px-3 py-2 bg-slate-50 rounded-lg text-sm text-slate-800">{owner.role || '-'}</p></div>
                </div>
              </div>
            </div>
          )}

          {/* Vehicles */}
          {activeTab === 'vehicles' && (
            <div className="space-y-3">
              {vehicles.length === 0 ? (
                <p className="text-sm text-slate-400 italic py-4">Keine Fahrzeuge registriert</p>
              ) : vehicles.map((v, idx) => (
                <div key={v.id || idx} className="border border-slate-200 rounded-xl p-4" data-testid={`company-vehicle-${v.id || idx}`}>
                  <div className="grid grid-cols-4 gap-3 text-sm">
                    <div><span className="text-slate-400 text-xs block">Hersteller</span><p className="text-slate-800">{v.make || '-'}</p></div>
                    <div><span className="text-slate-400 text-xs block">Modell</span><p className="text-slate-800">{v.model || '-'}</p></div>
                    <div><span className="text-slate-400 text-xs block">Baujahr</span><p className="text-slate-800">{v.year || '-'}</p></div>
                    <div><span className="text-slate-400 text-xs block">Farbe</span><p className="text-slate-800">{v.color || '-'}</p></div>
                    <div><span className="text-slate-400 text-xs block">Kennzeichen</span><p className="text-slate-800">{v.plate_number || '-'}</p></div>
                    <div><span className="text-slate-400 text-xs block">Typ</span><p className="text-slate-800 capitalize">{v.vehicle_type || '-'}</p></div>
                    <div><span className="text-slate-400 text-xs block">Passagiere</span><p className="text-slate-800">{v.passengers || '-'}</p></div>
                    <div><span className="text-slate-400 text-xs block">Gepäck</span><p className="text-slate-800">{v.luggage || '-'}</p></div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Drivers */}
          {activeTab === 'drivers' && (
            <div className="space-y-3">
              {drivers.length === 0 ? (
                <p className="text-sm text-slate-400 italic py-4">Keine Fahrer registriert</p>
              ) : drivers.map((d, idx) => (
                <div key={d.id || idx} className="border border-slate-200 rounded-xl p-4 flex items-center justify-between" data-testid={`company-driver-${d.id || idx}`}>
                  <div>
                    <p className="font-medium text-slate-800 text-sm">{d.name}</p>
                    <p className="text-xs text-slate-500">{d.phone} | {d.email || '-'}</p>
                  </div>
                  {d.assigned_vehicle_id && (
                    <span className="px-2 py-0.5 bg-blue-50 text-blue-600 rounded text-xs">Fahrzeug zugewiesen</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
